import { z } from "zod";
import { Id, IsoDate, Page, PageQuery } from "./common.ts";
import { AiLabels, CommenterMark, CommentEvent, ContactBlockState, ContactProfile, PostRef } from "./domain.ts";
import type { RouteDef } from "./routes.ts";

/**
 * Customer 360 (ALP-79): everything one person did with the brand, from any place that shows their username.
 *
 * The timeline unifies (newest first, cursor-paged): their comments and our replies (with post ref + thumbnail and the
 * deleted / hidden / archived / pinned states), DMs in and out, message reactions, story replies, AI drafts + approvals,
 * automation runs on their items, flags / marks / tags, notes and enrichment snapshots.
 *
 * Instagram does NOT expose which users liked a post: likes are never part of a person's timeline (`likesAvailable` is
 * always false and the UI says "Instagram doesn't share who liked a post"). Mentions are not stored yet (Instagram Login
 * delivers them inside `comments` webhooks without a separate record), so the `mention` type exists for later channels.
 */
// ---------------------------------------------------------------- contact names (QA F15)
/** Shown when nothing better is known yet (the worker resolves the name from the channel soon after). */
export const UNKNOWN_CONTACT_NAME = "Instagram user";
/** A raw channel id (Instagram-scoped id, PSID: 6+ digits) is never a display name. */
export const isRawChannelId = (s: string | null | undefined): boolean => !!s && /^\d{6,}$/.test(s.trim());
/** Does a stored contact display name need replacing (empty, or a raw numeric channel id)? */
export const needsContactName = (s: string | null | undefined): boolean => !(s ?? "").trim() || isRawChannelId(s);
/**
 * The name to show for a person: their name, else `@username`, else a neutral label. NEVER a raw numeric id (DM contacts
 * arrive with only an Instagram-scoped id until a username is known).
 */
export function contactDisplayName(displayName: string | null | undefined, ...handles: (string | null | undefined)[]): string {
  const name = (displayName ?? "").trim();
  if (!needsContactName(name)) return name;
  for (const h of handles) {
    const u = (h ?? "").trim().replace(/^@+/, "");
    if (u && !isRawChannelId(u)) return `@${u}`;
  }
  return UNKNOWN_CONTACT_NAME;
}

export const TIMELINE_TYPES = [
  "comment", "reply", "dm_in", "dm_out", "reaction", "story_reply", "mention",
  "ai_draft", "approval", "automation_run", "flag", "mark", "note", "enrichment",
] as const;
export const TimelineType = z.enum(TIMELINE_TYPES);
export type TimelineTypeT = (typeof TIMELINE_TYPES)[number];
/** Chip groups the UI filters by (one chip may cover several types). */
export const TIMELINE_GROUPS = {
  comments: ["comment", "reply", "mention"],
  dms: ["dm_in", "dm_out", "reaction", "story_reply"],
  automation: ["ai_draft", "approval", "automation_run"],
  team: ["flag", "mark", "note", "enrichment"],
} as const satisfies Record<string, readonly TimelineTypeT[]>;

export const TimelineActor = z.object({
  type: z.enum(["contact", "account", "user", "automation", "ai", "system"]),
  id: z.string().nullable(),
  name: z.string().nullable(),
});
/** Where the item lives: the UI deep-links to its quick panel (`?peek=…`, comment panel, conversation, job). */
export const TimelineRef = z.object({
  kind: z.enum(["comment", "conversation", "message", "run", "approval", "job", "note", "contact"]),
  id: Id,
  /** For messages: the conversation to open. For replies: the comment replied to. */
  parentId: Id.nullable().optional(),
});
export const TimelineItem = z.object({
  /** Stable, unique across types: `<type>:<uuid>` (also the cursor tiebreaker). */
  id: z.string().max(120),
  type: TimelineType,
  at: IsoDate,
  accountId: Id.nullable(),
  post: PostRef.nullable(),
  /** User-generated text (comment, DM, note) or a short system summary. */
  text: z.string().nullable(),
  actor: TimelineActor,
  ref: TimelineRef,
  /** Item states (comments: visible/hidden/deleted + archived/pinned; DMs: unsent/edited). */
  state: z.object({
    status: z.string().nullable().optional(),
    hidden: z.boolean().optional(),
    deleted: z.boolean().optional(),
    archived: z.boolean().optional(),
    pinned: z.boolean().optional(),
    edited: z.boolean().optional(),
  }).default({}),
  /** Outcome of what we did (replies, approvals, runs): e.g. sent / failed + reason, approved / rejected / expired. */
  outcome: z.object({ status: z.string(), error: z.string().nullable().optional() }).nullable().optional(),
  labels: AiLabels.partial().nullable().optional(),
  /** Type-specific extras: reaction emoji, flag key, mark value, enrichment source, rule name, attachments… */
  data: z.record(z.string(), z.unknown()).default({}),
  /** Comments: the latest thing that happened to it and who did it (ALP-84, same as Comment.lastAction). */
  lastAction: CommentEvent.nullable().optional(),
});
export type TimelineItemT = z.infer<typeof TimelineItem>;

export const ContactTimelineQuery = PageQuery.extend({
  /** Comma-separated TimelineType list (default: all). */
  types: z.string().max(300).optional(),
  accountId: Id.optional(),
  postId: Id.optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
});

export const ContactSummary = z.object({
  contactId: Id,
  firstSeenAt: IsoDate.nullable(),
  lastSeenAt: IsoDate.nullable(),
  /** Every TimelineType key (0 when none). */
  counts: z.record(TimelineType, z.number().int()),
  /** Weekly sentiment of their comments + inbound DMs (oldest first, last 12 weeks with activity). */
  sentimentTrend: z.array(z.object({ week: z.string(), positive: z.number().int(), neutral: z.number().int(), negative: z.number().int() })),
  /** Share of their comments/DM threads we answered (null = nothing to answer yet). */
  replyRate: z.number().min(0).max(1).nullable(),
  /** Average time from their message/comment to our first reply, seconds (null = never replied). */
  avgResponseSeconds: z.number().nullable(),
  accounts: z.array(z.object({ accountId: Id, username: z.string(), count: z.number().int() })),
  posts: z.array(PostRef.extend({ count: z.number().int() })),
  /** Always false on Instagram: the platform does not share who liked a post. */
  likesAvailable: z.literal(false),
});
export type ContactSummaryT = z.infer<typeof ContactSummary>;

/** Person header: everything the profile needs in one call (the Contact plus marks, notes count, IG link). */
export const ContactSearchQuery = z.object({
  q: z.string().trim().min(1).max(100),
  limit: z.coerce.number().int().min(1).max(25).default(8),
  /** Also match the text of their comments / DMs (slower; the palette sends false, the People page true). */
  keyword: z.enum(["true", "false", "1", "0"]).optional(),
});
export const ContactSearchHit = z.object({
  contactId: Id,
  displayName: z.string(),
  handle: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  mark: CommenterMark.nullable(),
  lastSeenAt: IsoDate,
  matched: z.enum(["handle", "name", "keyword"]),
  /** For keyword hits: a short excerpt of the matching comment / DM (untrusted text). */
  snippet: z.string().nullable(),
  profileUrl: z.string().url().nullable(),
  blocked: z.boolean().optional(), // blocked in Malfak (any scope)
});
export type ContactSearchHitT = z.infer<typeof ContactSearchHit>;

export const ContactNote = z.object({
  id: Id,
  contactId: Id,
  body: z.string(),
  author: z.object({ id: Id.nullable(), name: z.string().nullable() }),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
export type ContactNoteT = z.infer<typeof ContactNote>;
export const ContactNoteInput = z.object({ body: z.string().trim().min(1).max(4000) });

/** Person page header (`contact360`): profile + marks/tags + IG link; enrichment as stored (never fetched on read). */
export const ContactProfile360 = z.object({
  contactId: Id,
  displayName: z.string(),
  handle: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  profileUrl: z.string().url().nullable(),
  mark: CommenterMark.nullable(),
  flags: z.array(z.string()),
  tags: z.array(z.string()),
  profile: ContactProfile.nullable(),
  notesCount: z.number().int(),
  /** Block in Malfak (people.ts); null = not blocked. The real Instagram block is a deep link to `profileUrl`. */
  block: ContactBlockState.nullable().optional(),
});

// ---------------------------------------------------------------- onboarding done per org (ALP-79 §3)
export const OnboardingState = z.enum(["pending", "finished", "skipped"]);
export const OrgOnboarding = z.object({ state: OnboardingState, completedAt: IsoDate.nullable() });
export type OrgOnboardingT = z.infer<typeof OrgOnboarding>;
export const UpdateOnboardingRequest = z.object({ state: OnboardingState });

// ---------------------------------------------------------------- routes (spread into `routes`)
const route = <P extends z.ZodTypeAny, Q extends z.ZodTypeAny, B extends z.ZodTypeAny, R extends z.ZodTypeAny>(d: RouteDef<P, Q, B, R>) => d;
const OrgP = z.object({ orgId: z.string().uuid() });
const ContactP = OrgP.extend({ contactId: z.string().uuid() });
const NoteP = ContactP.extend({ noteId: z.string().uuid() });

export const customer360Routes = {
  contactSearch: route({ method: "GET", path: "/v1/orgs/:orgId/contacts/search", params: OrgP, query: ContactSearchQuery, response: z.object({ items: z.array(ContactSearchHit) }), summary: "Username / handle / name prefix (+ optional keyword) typeahead, org-scoped, indexed" }),
  contact360: route({ method: "GET", path: "/v1/orgs/:orgId/contacts/:contactId/360", params: ContactP, response: ContactProfile360, summary: "Person header: profile, marks, flags, tags, notes count, Instagram link" }),
  contactTimeline: route({ method: "GET", path: "/v1/orgs/:orgId/contacts/:contactId/timeline", params: ContactP, query: ContactTimelineQuery, response: Page(TimelineItem), summary: "Every interaction of one person, newest first (filters: types, account, post, time)" }),
  contactSummary: route({ method: "GET", path: "/v1/orgs/:orgId/contacts/:contactId/summary", params: ContactP, response: ContactSummary, summary: "First/last seen, counts by type, sentiment trend, reply rate, response time, accounts and posts" }),
  contactNotes: route({ method: "GET", path: "/v1/orgs/:orgId/contacts/:contactId/notes", params: ContactP, response: z.array(ContactNote), summary: "Team notes about a person, newest first" }),
  createContactNote: route({ method: "POST", path: "/v1/orgs/:orgId/contacts/:contactId/notes", params: ContactP, body: ContactNoteInput, response: ContactNote, status: 201, summary: "Add a note (editor+, audited)" }),
  updateContactNote: route({ method: "PATCH", path: "/v1/orgs/:orgId/contacts/:contactId/notes/:noteId", params: NoteP, body: ContactNoteInput, response: ContactNote, summary: "Edit a note (editor+, audited)" }),
  deleteContactNote: route({ method: "DELETE", path: "/v1/orgs/:orgId/contacts/:contactId/notes/:noteId", params: NoteP, response: z.object({ ok: z.literal(true) }), summary: "Delete a note (editor+, audited)" }),
  updateOnboarding: route({ method: "PUT", path: "/v1/orgs/:orgId/onboarding", params: OrgP, body: UpdateOnboardingRequest, response: OrgOnboarding, summary: "Mark the org's onboarding finished / skipped (or pending again)" }),
} as const;
